import { inject } from '@angular/core'
import { CanActivateFn, Router, UrlTree } from '@angular/router'
import { Store } from '@ngxs/store'
import { filter, map, Observable, take } from 'rxjs'
import { UserState } from '../../../store/user/user.state'
import { FeatureFlagService } from './feature-flag.service'
import {
  hasResolvableClinicalRole,
  PaRoleFields
} from '../data/pa-clinical-role.predicate'
import { RouteName } from '../../../core/enums/route-name.enum'

/**
 * PA route gate: the feature gate (env flag + per-user allowlist) AND a resolvable clinical role. A denied
 * deep link silently redirects to the patients list. Waits for the fully resolved user (id and `_userType`
 * arrive together with the role fields) so an allowed user is not bounced mid-login.
 */
type PaGuardUser = PaRoleFields & {
  id?: string
  _id?: string
  _userType?: string | null
}

export const paFlagGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const featureFlag = inject(FeatureFlagService)
  const store = inject(Store)
  const router = inject(Router)

  return store.select(UserState.currentUser).pipe(
    map(user => user as PaGuardUser | null),
    filter(
      (user): user is PaGuardUser =>
        (user?.id ?? user?._id) != null && user?._userType != null
    ),
    take(1),
    map(user =>
      hasResolvableClinicalRole(user) &&
      featureFlag.isPaUnlockedFor(user.id ?? user._id ?? null)
        ? true
        : router.parseUrl('/' + RouteName.Patients)
    )
  )
}
